import type { Building } from "building-types/building/buildingTypes";
import {
  SiteShapeType,
  type ContourSiteShape,
  type DrivewaySiteShape,
  type RoadSiteShape,
} from "building-types/site/siteShape";
import type { LineStyle } from "building-types/views/Primitive2DOutput";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import colors from "common/constants/colorsOld";
import { sheetInchesNew } from "common/utils/nominalNumbers";
import { LW_THIN, LW_MEDIUM } from "doc/plans/constants";
import { polyLine2Copy, polyLine2Start } from "geom/polyLine2";
import { makeSheetGeometryFromLabels } from "lib/house/plans/entities/planLabels";
import type { Label } from "lib/house/plans/label";
import { labelNew } from "lib/house/plans/label";

interface PlanSiteProps {
  building: Building;
  levelId: string;
  color?: string;
  lineStyleOverride?: LineStyle;
}

export function makeSheetGeometryFromSiteShapeRoads({
  building,
  color = colors.stoneDark,
  lineStyleOverride,
}: PlanSiteProps): SheetGeometry[] {
  const result: SheetGeometry[] = [];

  if (!building.sites) {
    return result;
  }

  for (const site of Object.values(building.sites)) {
    const roadShapes = site.siteShapes.filter((shape) => shape.type === SiteShapeType.ROAD) as RoadSiteShape[];

    for (const roadShape of roadShapes) {
      const roadGeometry = polyLine2Copy(roadShape.geom);

      result.push(
        sheetPolylineGeometryNew({
          handle: { type: "SITE_SHAPE", id: roadShape.id, parent: { type: "SITE", id: roadShape.parentId } },
          paths: [roadGeometry],
          linewidth: lineStyleOverride?.linewidth ?? LW_MEDIUM,
          fill: false,
          strokeColor: lineStyleOverride?.strokeColor ?? color,
        }),
      );
    }
  }

  return result;
}

/**
 * Generates sheet geometry for site shape contours
 * @param param0 Plan site properties
 * @returns Array of sheet geometries
 */
export function makeSheetGeometryFromSiteShapeContours({
  building,
  color = colors.stoneDark,
  lineStyleOverride,
}: PlanSiteProps): SheetGeometry[] {
  const result: SheetGeometry[] = [];

  if (!building.sites) {
    return result;
  }

  for (const site of Object.values(building.sites)) {
    const siteShapes = site.siteShapes || [];
    const contourShapes = siteShapes.filter((shape) => shape.type === SiteShapeType.CONTOUR) as ContourSiteShape[];

    for (const contourShape of contourShapes) {
      const contourGeometry = polyLine2Copy(contourShape.geom);

      result.push(
        sheetPolylineGeometryNew({
          handle: { type: "SITE_SHAPE", id: contourShape.id, parent: { type: "SITE", id: contourShape.parentId } },
          paths: [contourGeometry],
          linewidth: lineStyleOverride?.linewidth ?? LW_THIN,
          fill: false,
          strokeColor: lineStyleOverride?.strokeColor ?? color,
        }),
      );
    }
  }

  return result;
}

export function makeSheetGeometryFromSiteShapeDriveways({
  building,
  lineStyleOverride,
}: PlanSiteProps): SheetGeometry[] {
  const result: SheetGeometry[] = [];

  if (!building.sites) {
    return result;
  }

  for (const site of Object.values(building.sites)) {
    const siteShapes = site.siteShapes || [];
    const drivewayShapes = siteShapes.filter((shape) => shape.type === SiteShapeType.DRIVEWAY) as DrivewaySiteShape[];

    for (const drivewayShape of drivewayShapes) {
      const drivewayGeometry = polyLine2Copy(drivewayShape.geom);

      result.push(
        sheetPolylineGeometryNew({
          handle: { type: "SITE_SHAPE", id: drivewayShape.id, parent: { type: "SITE", id: drivewayShape.parentId } },
          paths: [drivewayGeometry],
          linewidth: lineStyleOverride?.linewidth ?? LW_THIN,
          fill: true,
          fillColor: colors.stoneLight,
        }),
      );
    }
  }

  return result;
}

export function makeSheetGeometryFromSiteShapeElevationTags({ building }: PlanSiteProps): SheetGeometry[] {
  const result: SheetGeometry[] = [];

  if (!building.sites) {
    return result;
  }

  for (const site of Object.values(building.sites)) {
    const siteShapes = site.siteShapes || [];
    const contourShapes = siteShapes.filter((shape) => shape.type === SiteShapeType.CONTOUR) as ContourSiteShape[];

    const labels: Label[] = [];

    for (const contourShape of contourShapes) {
      const contourGeometry = polyLine2Copy(contourShape.geom);

      const elevationText = `${contourShape.zHeight.toFixed(2)}'`;

      const labelPosition = polyLine2Start(contourGeometry);
      if (contourShape.parentId && labelPosition) {
        labels.push(
          labelNew({
            handle: { type: "SITE_SHAPE", id: contourShape.id, parent: { type: "SITE", id: contourShape.parentId } },
            text: elevationText,
            centerX: contourGeometry[0],
            centerY: contourGeometry[1],
            fontSize: sheetInchesNew(0.125),
          }),
        );
      }
    }

    result.push(...makeSheetGeometryFromLabels({ handle: { type: "SITE", id: site.id }, labels }));
  }

  return result;
}
